import type { BusinessReportOutcome, BusinessReportOutcomeResult } from "@/modules/snapshots/businessReports.types";
import type {
  PivotAggregation,
  PivotChartSelection,
  PivotChartType,
  PivotDatePeriod,
  PivotConfig,
  PivotFieldSchema,
  PivotFieldType,
  PivotResult,
  PivotResultRow,
  PivotValueFormat,
  PivotValueField,
} from "./pivotTypes";

export const DOCUMENT_COUNT_FIELD: PivotValueField = {
  key: "__document_count__",
  label: "Document Count",
  aggregation: "count",
  format: "integer",
};

interface BuildPivotResultArgs {
  outcomes: BusinessReportOutcome[];
  rowFieldKey: string;
  rowFieldType: PivotFieldType;
  valueFields: PivotValueField[];
  chartType?: PivotChartSelection;
  seriesFieldKey?: string;
  seriesFieldLabel?: string;
  datePeriod?: PivotDatePeriod;
  normalisationMapping?: Record<string, string>;
}

interface FieldAccumulator {
  sum: number;
  count: number;
}

interface GroupAccumulator {
  label: string;
  sortKey: number | string;
  sourceCount: number;
  values: Record<string, FieldAccumulator>;
}

const AGGREGATION_LABELS: Record<PivotAggregation, string> = {
  count: "Count",
  sum: "Sum",
  average: "Average",
};

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DATE_FIELD_NAME_PATTERN = /\b(date|dated|deadline|expiry|expiration|expires)\b/i;
const NUMERIC_FIELD_NAME_PATTERN =
  /\b(amount|budget|cost|expense|fee|income|payment|price|rate|revenue|salary|total|value)\b/i;
const CURRENCY_FIELD_NAME_PATTERN =
  /\b(amount|budget|charged|cost|expense|fee|income|payment|price|revenue|salary|total|value)\b/i;
const INTEGER_FIELD_NAME_PATTERN = /\b(count|headcount|number|quantity|qty|units?)\b/i;
const PERCENTAGE_FIELD_NAME_PATTERN = /\b(percent|percentage|rate)\b/i;
const CURRENCY_VALUE_PATTERN = /[$£€¥₹]|\b(?:USD|GBP|EUR|AUD|CAD|NZD)\b/i;
const PERCENTAGE_VALUE_PATTERN = /%|\bpercent(?:age)?\b/i;
const DATE_VALUE_TOKEN_PATTERN =
  /\b(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|(?:Jan|January|Feb|February|Mar|March|Apr|April|May|Jun|June|Jul|July|Aug|August|Sep|Sept|September|Oct|October|Nov|November|Dec|December)\s+\d{1,2},?\s+\d{4}|\d{1,2}\s+(?:Jan|January|Feb|February|Mar|March|Apr|April|May|Jun|June|Jul|July|Aug|August|Sep|Sept|September|Oct|October|Nov|November|Dec|December)\s+\d{4})\b/i;
const CITATION_MARKER_PATTERN =
  /\s*\[(?:Id:\s*)?(?:\d+(?:,\s*(?:Id:\s*)?\d+)*|[\w-]{12,}(?:[\s,]+[\w-]{12,})*)\]\s*[.,;:]?/gi;
const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\([^)]+\)/g;
const MARKDOWN_DECORATION_PATTERN = /[*_`~]/g;

const findCell = (outcome: BusinessReportOutcome, fieldKey: string): BusinessReportOutcomeResult | undefined =>
  outcome.result?.find(cell => cell.name === fieldKey && !cell.is_note);

const isFreeTextPivotCell = (cell: BusinessReportOutcomeResult): boolean => cell.data_type === null && !cell.is_date;

const isExcludedPivotCell = (cell: BusinessReportOutcomeResult): boolean =>
  cell.is_note || cell.traffic_light || cell.rating !== null || isFreeTextPivotCell(cell);

export const stripPivotValueCitations = (value: string): string =>
  value.replace(CITATION_MARKER_PATTERN, " ").replace(/\s+/g, " ").trim();

export const getPivotNormalisationValue = (value: string): string =>
  value
    .split(/\r?\n/)
    .map(line =>
      stripPivotValueCitations(line)
        .replace(MARKDOWN_LINK_PATTERN, "$1")
        .replace(MARKDOWN_DECORATION_PATTERN, "")
        .replace(/\s+/g, " ")
        .trim(),
    )
    .find(Boolean) ?? "";

export const parsePivotNumber = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  const cleaned = value.replace(/[£$,\s]/g, "");
  const match = cleaned.match(/-?\d+(?:\.\d+)?[kKmMbB]?/);
  if (!match) return null;

  const suffix = match[0].slice(-1);
  const numericText = /[kKmMbB]/.test(suffix) ? match[0].slice(0, -1) : match[0];
  const parsed = Number(numericText);
  if (!Number.isFinite(parsed)) return null;

  if (suffix === "k" || suffix === "K") return parsed * 1000;
  if (suffix === "m" || suffix === "M") return parsed * 1000000;
  if (suffix === "b" || suffix === "B") return parsed * 1000000000;
  return parsed;
};

const parsePivotDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;

  const trimmed = value.trim();
  const createDate = (year: number, month: number, day: number): Date | null => {
    const date = new Date(year, month - 1, day);
    if (
      Number.isNaN(date.getTime()) ||
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }
    return date;
  };

  const separatedDate = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (separatedDate) {
    const [, first, second, year] = separatedDate;
    return (
      createDate(Number(year), Number(second), Number(first)) ?? createDate(Number(year), Number(first), Number(second))
    );
  }

  const isoDateOnly = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateOnly) {
    const [, year, month, day] = isoDateOnly;
    return createDate(Number(year), Number(month), Number(day));
  }

  if (/^[\d/-]+$/.test(trimmed)) return null;

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isLikelyPivotDateValue = (value: string, allowEmbedded: boolean): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;

  if (DATE_VALUE_TOKEN_PATTERN.test(trimmed) && parsePivotDate(trimmed)) return true;
  if (!allowEmbedded) return false;

  const embeddedDate = trimmed.match(DATE_VALUE_TOKEN_PATTERN)?.[0];
  return embeddedDate ? parsePivotDate(embeddedDate) !== null : false;
};

export const derivePivotFieldSchema = (outcomes: BusinessReportOutcome[]): PivotFieldSchema[] => {
  const fields = new Map<
    string,
    {
      label: string;
      order: number;
      hasDate: boolean;
      dateCount: number;
      numericCount: number;
      nonEmptyCount: number;
      hasExplicitDate: boolean;
      hasExplicitNumeric: boolean;
      hasExplicitCategorical: boolean;
      hasExplicitNonAggregatable: boolean;
      values: string[];
    }
  >();

  for (const outcome of outcomes) {
    for (const cell of outcome.result ?? []) {
      if (isExcludedPivotCell(cell)) continue;

      const hasValue = typeof cell.result === "string" ? cell.result.trim().length > 0 : cell.result != null;
      const isNamedDateField = DATE_FIELD_NAME_PATTERN.test(cell.name);
      const isDate = cell.is_date || (hasValue && isLikelyPivotDateValue(cell.date ?? cell.result, isNamedDateField));
      const isNumeric = hasValue && parsePivotNumber(cell.result) !== null;
      const explicitDataType = cell.data_type;
      const existing = fields.get(cell.name);
      fields.set(cell.name, {
        label: cell.name,
        order: Math.min(existing?.order ?? cell.order, cell.order),
        hasDate: Boolean(existing?.hasDate || cell.is_date),
        dateCount: (existing?.dateCount ?? 0) + (isDate ? 1 : 0),
        numericCount: (existing?.numericCount ?? 0) + (isNumeric ? 1 : 0),
        nonEmptyCount: (existing?.nonEmptyCount ?? 0) + (hasValue ? 1 : 0),
        hasExplicitDate: Boolean(existing?.hasExplicitDate || explicitDataType === "date"),
        hasExplicitNumeric: Boolean(existing?.hasExplicitNumeric || explicitDataType === "numeric"),
        hasExplicitCategorical: Boolean(existing?.hasExplicitCategorical || explicitDataType === "categorical"),
        hasExplicitNonAggregatable: Boolean(
          existing?.hasExplicitNonAggregatable || explicitDataType === "currency" || explicitDataType === "relevancy",
        ),
        values: [...(existing?.values ?? []), ...(hasValue ? [cell.result] : [])],
      });
    }
  }

  return [...fields.entries()]
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([key, field]) => {
      const explicitType: PivotFieldType | null = field.hasExplicitNonAggregatable
        ? "categorical"
        : field.hasExplicitDate
          ? "date"
          : field.hasExplicitNumeric
            ? "numeric"
            : field.hasExplicitCategorical
              ? "categorical"
              : null;
      const inferredType: PivotFieldType =
        field.hasDate ||
        (field.nonEmptyCount > 0 &&
          field.dateCount > 0 &&
          (field.dateCount === field.nonEmptyCount || DATE_FIELD_NAME_PATTERN.test(field.label)))
          ? "date"
          : field.nonEmptyCount > 0 &&
              field.numericCount > 0 &&
              (field.numericCount === field.nonEmptyCount || NUMERIC_FIELD_NAME_PATTERN.test(field.label))
            ? "numeric"
            : "categorical";

      const type = explicitType ?? inferredType;
      const format = getPivotFieldFormat(field.label, type, field.values);

      return {
        key,
        label: field.label,
        type,
        ...(format ? { format } : {}),
      };
    });
};

const getPivotFieldFormat = (
  fieldLabel: string,
  fieldType: PivotFieldType,
  values: string[],
): PivotValueFormat | undefined => {
  if (fieldType !== "numeric") return undefined;

  if (values.some(value => PERCENTAGE_VALUE_PATTERN.test(value)) || PERCENTAGE_FIELD_NAME_PATTERN.test(fieldLabel)) {
    return "percentage";
  }

  if (values.some(value => CURRENCY_VALUE_PATTERN.test(value)) || CURRENCY_FIELD_NAME_PATTERN.test(fieldLabel)) {
    return "currency";
  }

  const numericValues = values.map(value => parsePivotNumber(value)).filter((value): value is number => value !== null);
  if (
    numericValues.length > 0 &&
    (INTEGER_FIELD_NAME_PATTERN.test(fieldLabel) || numericValues.every(Number.isInteger))
  ) {
    return "integer";
  }

  return undefined;
};

const getWeekNumber = (date: Date): number => {
  const firstDay = new Date(date.getFullYear(), 0, 1);
  const dayOffset = Math.floor((date.getTime() - firstDay.getTime()) / 86400000);
  return Math.floor((dayOffset + firstDay.getDay()) / 7) + 1;
};

const getDateGroup = (date: Date, period: PivotDatePeriod): { label: string; sortKey: number } => {
  const year = date.getFullYear();
  const month = date.getMonth();

  if (period === "week") {
    const week = getWeekNumber(date);
    return { label: `Week ${week} ${year}`, sortKey: year * 100 + week };
  }

  if (period === "quarter") {
    const quarter = Math.floor(month / 3) + 1;
    return { label: `Q${quarter} ${year}`, sortKey: year * 10 + quarter };
  }

  return { label: `${MONTH_LABELS[month]} ${year}`, sortKey: year * 100 + month };
};

const getValueLabel = (valueField: PivotValueField): string => {
  if (valueField.key === DOCUMENT_COUNT_FIELD.key) return DOCUMENT_COUNT_FIELD.label;
  return `${AGGREGATION_LABELS[valueField.aggregation]} of ${valueField.label}`;
};

const getValueFormat = (valueField: PivotValueField): PivotValueFormat | undefined => {
  if (valueField.aggregation === "count") return "integer";
  return valueField.format;
};

const formatDecimal = (value: number): string =>
  value.toLocaleString(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  });

export const formatPivotValue = (value: number, format?: PivotValueFormat): string => {
  if (format === "currency") {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value);
  }

  if (format === "percentage") return `${formatDecimal(value)}%`;
  if (format === "integer") return Math.round(value).toLocaleString();
  return formatDecimal(value);
};

const getGroupForOutcome = (
  outcome: BusinessReportOutcome,
  rowFieldKey: string,
  rowFieldType: PivotFieldType,
  datePeriod: PivotDatePeriod,
  normalisationMapping: Record<string, string> | undefined,
): { label: string; sortKey: number | string } | null => {
  const rowCell = findCell(outcome, rowFieldKey);
  if (!rowCell) return null;

  if (rowFieldType === "date") {
    const date = parsePivotDate(rowCell.date ?? rowCell.result);
    return date ? getDateGroup(date, datePeriod) : null;
  }

  const rawValue = rowCell.result ?? "";
  const label = normalisationMapping?.[rawValue] ?? rawValue;
  return { label: label || "(Blank)", sortKey: label || "(Blank)" };
};

const getSeriesLabelForOutcome = (outcome: BusinessReportOutcome, seriesFieldKey: string): string => {
  const seriesCell = findCell(outcome, seriesFieldKey);
  return seriesCell?.result || "(Blank)";
};

const ensureFieldAccumulator = (group: GroupAccumulator, valueLabel: string): FieldAccumulator => {
  group.values[valueLabel] ??= { sum: 0, count: 0 };
  return group.values[valueLabel];
};

const addValueToGroup = (
  group: GroupAccumulator,
  outcome: BusinessReportOutcome,
  valueField: PivotValueField,
  valueLabel: string,
) => {
  const accumulator = ensureFieldAccumulator(group, valueLabel);

  if (valueField.key === DOCUMENT_COUNT_FIELD.key) {
    accumulator.sum += 1;
    accumulator.count += 1;
    return;
  }

  const numericValue = parsePivotNumber(findCell(outcome, valueField.key)?.result);
  if (numericValue === null) return;

  if (valueField.aggregation === "count") {
    accumulator.sum += 1;
  } else {
    accumulator.sum += numericValue;
  }
  accumulator.count += 1;
};

const finaliseValue = (field: PivotValueField, accumulator: FieldAccumulator | undefined): number => {
  if (!accumulator) return 0;
  if (field.aggregation === "average") return accumulator.count === 0 ? 0 : accumulator.sum / accumulator.count;
  return accumulator.sum;
};

const getChartType = (
  rowFieldType: PivotFieldType,
  rows: PivotResultRow[],
  valueFields: PivotValueField[],
  chartType: PivotChartSelection | undefined,
  hasSeriesField: boolean,
): PivotChartType => {
  if (chartType && chartType !== "auto") return chartType;
  if (rowFieldType === "date") return "line";
  if (hasSeriesField || valueFields.length >= 2) return "grouped-bar";
  if (valueFields[0]?.aggregation === "count" && rows.length <= 5) return "doughnut";
  return "bar";
};

export const buildPivotResult = ({
  outcomes,
  rowFieldKey,
  rowFieldType,
  valueFields,
  chartType,
  seriesFieldKey,
  datePeriod = "month",
  normalisationMapping,
}: BuildPivotResultArgs): PivotResult => {
  const hasSeriesField = Boolean(seriesFieldKey);
  const defaultValueLabels = valueFields.map(getValueLabel);
  const seriesValueField = valueFields[0];
  const seriesLabels: string[] = [];
  const valueLabels = hasSeriesField ? seriesLabels : defaultValueLabels;
  const groups = new Map<string, GroupAccumulator>();

  for (const outcome of outcomes) {
    const groupInfo = getGroupForOutcome(outcome, rowFieldKey, rowFieldType, datePeriod, normalisationMapping);
    if (!groupInfo) continue;

    const group = groups.get(groupInfo.label) ?? {
      label: groupInfo.label,
      sortKey: groupInfo.sortKey,
      sourceCount: 0,
      values: {},
    };

    group.sourceCount += 1;
    if (seriesFieldKey && seriesValueField) {
      const seriesLabel = getSeriesLabelForOutcome(outcome, seriesFieldKey);
      if (seriesLabel) {
        if (!seriesLabels.includes(seriesLabel)) seriesLabels.push(seriesLabel);
        addValueToGroup(group, outcome, seriesValueField, seriesLabel);
      }
    } else {
      valueFields.forEach((valueField, index) => addValueToGroup(group, outcome, valueField, valueLabels[index]));
    }
    groups.set(groupInfo.label, group);
  }

  const valueFormats = Object.fromEntries(
    (hasSeriesField
      ? valueLabels.map(
          valueLabel => [valueLabel, seriesValueField ? getValueFormat(seriesValueField) : undefined] as const,
        )
      : valueFields.map((valueField, index) => [valueLabels[index], getValueFormat(valueField)] as const)
    ).filter((entry): entry is readonly [string, PivotValueFormat] => entry[1] !== undefined),
  );

  const rows = [...groups.values()].map(group => ({
    label: group.label,
    values: Object.fromEntries(
      valueLabels.flatMap((valueLabel, index) => {
        const valueField = hasSeriesField ? seriesValueField : valueFields[index];
        return valueField ? [[valueLabel, finaliseValue(valueField, group.values[valueLabel])] as const] : [];
      }),
    ),
    sourceCount: group.sourceCount,
    sortKey: group.sortKey,
  }));

  const sortedRows = rows
    .sort((a, b) => {
      if (rowFieldType === "date") return Number(a.sortKey) - Number(b.sortKey);
      const firstValue = valueLabels[0];
      const diff = (b.values[firstValue] ?? 0) - (a.values[firstValue] ?? 0);
      return diff || a.label.localeCompare(b.label);
    })
    .map(({ sortKey: _sortKey, ...row }) => row);

  return {
    chartType: getChartType(rowFieldType, sortedRows, valueFields, chartType, hasSeriesField),
    rows: sortedRows,
    valueLabels,
    ...(Object.keys(valueFormats).length > 0 ? { valueFormats } : {}),
  };
};

export const getUniqueRawValuesForField = (outcomes: BusinessReportOutcome[], fieldKey: string): string[] => {
  const values = new Set<string>();
  for (const outcome of outcomes) {
    const cell = findCell(outcome, fieldKey);
    if (cell) values.add(cell.result ?? "");
  }
  return [...values].sort((a, b) => a.localeCompare(b));
};

export const getNormalisationRowsForField = (
  outcomes: BusinessReportOutcome[],
  fieldKey: string,
): Array<{ id: string; value: string }> =>
  [...new Set(getUniqueRawValuesForField(outcomes, fieldKey).map(getPivotNormalisationValue))]
    .sort((a, b) => a.localeCompare(b))
    .map((value, index) => ({
      id: `normalisation-${index}`,
      value,
    }));

export const getNormalisationCategoryOptions = (mapping: Record<string, string>): string[] =>
  [...new Set(Object.values(mapping).filter(value => value.trim().length > 0))].sort((a, b) => a.localeCompare(b));

export const getPivotTitle = (config: PivotConfig): string => {
  const valueLabel = config.valueFields.map(getValueLabel).join(" vs ");
  const periodLabel = config.rowFieldType === "date" && config.datePeriod ? ` (${config.datePeriod})` : "";
  return `${valueLabel} by ${config.rowFieldLabel}${periodLabel}`;
};

export const getPivotSubtitle = (result: PivotResult): string =>
  `${result.rows.length} group${result.rows.length === 1 ? "" : "s"}`;
